"use client";

import {PracticePageProfile} from "@/libwyze-shared/types";
import React, {ReactElement} from "react";
import {Button, Typography, useMediaQuery, useTheme} from "@mui/material";
import {AddOutlined, BusinessOutlined, ExpandMoreOutlined} from "@mui/icons-material";
import {
  DialogFragment,
  PageFragment,
  PageFragmentProps,
  PageFragmentState,
  PageFragmentStyleFlags
} from "@/shared/PageFragment";
import {PAGE_FRAGMENT_WIDTH, PD_LG, PD_SM, PD_XXSM, SZ_XXXLG} from "@/shared/dimens";
import {PracticesListView} from "./PracticesListView";
import {BaseApp} from "@/shared/BaseApp";
import {StyledBoxColumn} from "@/shared/StyledComponents";
import {Action, ACTION_SPACE} from "@/shared/types";

export type PracticeSelectFragmentProps = PageFragmentProps & {
  practices: PracticePageProfile[],
  selectedPractice: PracticePageProfile,
  setSelectedPractice: (practice: PracticePageProfile | null) => void,
  style?: any,
  linkPractice?: () => void,
}

type PracticeSelectFragmentState = PageFragmentState & {
  selectedPractice: PracticePageProfile,
}

export class PracticeSelectFragment extends DialogFragment<PracticeSelectFragmentProps, PracticeSelectFragmentState> {

  protected onCreateState(props: PracticeSelectFragmentProps): PracticeSelectFragmentState {
    return {
      ...super.onCreateState(props),
      selectedPractice: props.selectedPractice,
    };
  }

  componentDidUpdate(prevProps: Readonly<PracticeSelectFragmentProps>, prevState: Readonly<PracticeSelectFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    const actionButtonDisabled = !Boolean(this.state.selectedPractice);
    if (this.state.actionButtonDisabled !== actionButtonDisabled) {
      this.setActionButtonEnabled(!actionButtonDisabled);
    }
  }

  protected getTitle(): string | null | undefined {
    return "Select Practice";
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG | PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    return "Select";
  }

  onActionButtonClicked(event?): Promise<boolean> | void {
    this.props.setSelectedPractice(this.state.selectedPractice);
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx([
      ACTION_SPACE,
      this.props.linkPractice
        ? new Action("Link", () => {
          this.props.setSelectedPractice(null);
          setTimeout(() => this.props.linkPractice(), 1);
        }, AddOutlined).setVariant("contained")
        : null,
    ].filter(Boolean));
  }

  renderContent(): ReactElement {
    return <StyledBoxColumn style={{...this.props.style, padding: PD_SM}}>
      <PracticesListView
        practices={this.props.practices}
        selectedPractice={this.state.selectedPractice}
        setSelectedPractice={selectedPractice => this.setState({selectedPractice})}/>
    </StyledBoxColumn>
  }
}

export function PracticeSelect(props: {
  practices: PracticePageProfile[],
  selectedPractice: PracticePageProfile,
  setSelectedPractice: (practice: PracticePageProfile | null) => void,
  linkPractice?: () => void,
}): ReactElement {
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up("md"));
  return <Button
    startIcon={<BusinessOutlined/>}
    endIcon={<ExpandMoreOutlined/>}
    style={{
      flexShrink: 0,
      minWidth: 0,
      maxWidth: SZ_XXXLG,
      minHeight: 40,
      padding: `${PD_XXSM}px ${PD_LG}px`
    }}
    onClick={() => {
      const id = BaseApp.CONTEXT.showDialog(null, () =>
        <PracticeSelectFragment
          style={{width: "84dvw", minHeight: 360, maxWidth: PAGE_FRAGMENT_WIDTH}}
          practices={props.practices}
          selectedPractice={props.selectedPractice}
          setSelectedPractice={(practice) => {
            BaseApp.CONTEXT.hideDialog(id);
            if (practice) {
              props.setSelectedPractice(practice);
            }
          }}
          linkPractice={props.linkPractice}
        />);
    }}>
    {md
      ? (props.selectedPractice
        ? <Typography variant="body2" noWrap={true}>{props.selectedPractice.basicInfo.name}</Typography>
        : <Typography variant={"body2"}><i>Tap to select practice</i></Typography>)
      : null}
  </Button>
}